import { FilterList } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  Menu,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { ApiError, ApiErrorClass, Plan } from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { EditIcon, TrashIcon } from "src/grading/assets";
import { LoadingButton } from "src/grading/components";
import RFDataGrid from "src/grading/components/RFDataGrid/RFDataGrid";
import AthletesLayout from "src/grading/layout/AthletesLayout/AthletesLayout";
import { MY_LIBRARY_ROUTES } from "src/grading/routes/MyLibraryRoutes";
import { getRelativeTime } from "src/grading/utils";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  useDeletePlanTemplatesLibraryMutation,
  useGetPlanTemplatesLibraryQuery,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import useStyles from "./PlanTemplates.style";
import PlanTemplateModal from "./PlanTemplatesModal/PlanTemplatesModal";

const PlanTemplates = () => {
  const classes = useStyles();
  // const profile = useAppSelector(selectCurrentUser);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [limit, _setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(0);
  const { data: planTemplates, isFetching } = useGetPlanTemplatesLibraryQuery({
    limit,
    offset,
  });
  const [planTemplateModalOpen, setPlanTemplateModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPlanTemplate, setSelectedPlanTemplate] = useState<Plan | null>(
    null,
  );
  const [deletePlanTemplate, { isLoading: deletePlanTemplateLoading }] =
    useDeletePlanTemplatesLibraryMutation();
  const dispatch = useAppDispatch();
  const [deleteMenuAnchorEl, setDeleteMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const deleteMenuOpen = Boolean(deleteMenuAnchorEl);
  const history = useHistory();

  const [_total, _setTotal] = useState(0);

  const _handleOpenPlanTemplateModal = (planTemplate: Plan) => {
    setIsEditing(true);
    setPlanTemplateModalOpen(true);
    setSelectedPlanTemplate(planTemplate);
  };

  const handleClosePlanTemplateModal = () => {
    setPlanTemplateModalOpen(false);
    setSelectedPlanTemplate(null);
  };

  const handleDeletePlanTemplate = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    try {
      if (!selectedPlanTemplate?.id) {
        throw new ApiErrorClass({
          message: "Plan Template ID is required",
        });
      }
      await deletePlanTemplate(selectedPlanTemplate.id).unwrap();
      dispatch(
        showSnackbar({
          open: true,
          message: "Plan Template deleted successfully",
        }),
      );
      handleDeleteMenuClose(event);
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleDeleteMenuClose = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setSelectedPlanTemplate(null);
    setDeleteMenuAnchorEl(null);
  };

  return (
    <AthletesLayout>
      <Grid container className={classes.container}>
        {/* ATHLETES HEADER CONTAINER */}
        <Grid container item alignItems={"center"}>
          <Grid item>
            <Grid container alignItems={"center"}>
              <Typography variant="h2">My Library | Plan Templates</Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* FILTERS */}
        <Grid
          container
          alignItems={"center"}
          justifyContent={"flex-end"}
          columnGap={2}
        >
          <Grid item>
            <Button variant="outlined" className={classes.sortButton}>
              <Typography variant="button" fontSize={"14px"}>
                Sort
              </Typography>
              <FilterList />
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              className={classes.addNewButton}
              onClick={() => {
                setIsEditing(false);
                setPlanTemplateModalOpen(true);
              }}
            >
              <Typography variant="button" fontSize={"14px"}>
                Add New
              </Typography>
            </Button>
          </Grid>
        </Grid>

        {/* DATA GRID */}
        <RFDataGrid
          rows={planTemplates?.data?.plans || []}
          columns={[
            {
              field: "name",
              headerName: "Plan Template Name",
              flex: 2,
              editable: false,
              sortable: true,
              filterable: false,
              minWidth: 300,
              renderHeader: () => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="#666"
                  fontWeight={"bold"}
                >
                  Plan Template Name
                </Typography>
              ),
              renderCell: ({ row }) => (
                <Typography
                  variant="caption"
                  fontSize={"24px"}
                  color="black"
                  fontWeight={"bold"}
                >
                  {row.name}
                </Typography>
              ),
            },
            {
              field: "weekCount",
              headerName: "Plan Weeks",
              flex: 0.5,
              editable: false,
              sortable: true,
              filterable: false,
              minWidth: 120,
              maxWidth: 150,
              align: "center",
              headerAlign: "center",
              renderHeader: () => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="#666"
                  fontWeight={"bold"}
                >
                  Plan Weeks
                </Typography>
              ),
              renderCell: ({ row }) => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="black"
                  fontWeight={"bold"}
                >
                  {row.weekCount}
                </Typography>
              ),
            },
            {
              field: "updatedAt",
              headerName: "Last update",
              flex: 0.7,
              editable: false,
              sortable: true,
              filterable: false,
              minWidth: 150,
              maxWidth: 180,
              align: "center",
              headerAlign: "center",
              renderHeader: () => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="#666"
                  fontWeight={"bold"}
                >
                  Last update
                </Typography>
              ),
              renderCell: ({ row }) => (
                <Typography
                  variant="caption"
                  fontSize={"18px"}
                  color="black"
                  fontWeight={"bold"}
                >
                  {getRelativeTime(new Date(row.updatedAt).getTime())}
                </Typography>
              ),
            },
            {
              field: "",
              flex: 0.4,
              editable: false,
              sortable: false,
              filterable: false,
              minWidth: 100,
              maxWidth: 120,
              renderCell: ({ row }) => (
                <Grid
                  container
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() =>
                        history.push(
                          MY_LIBRARY_ROUTES.PLAN_TEMPLATE.replace(
                            ":id",
                            row.id,
                          ),
                        )
                      }
                    >
                      <EditIcon stroke="black" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        setDeleteMenuAnchorEl(event.currentTarget);
                        setSelectedPlanTemplate(row);
                      }}
                    >
                      <TrashIcon stroke="black" />
                      <Menu
                        id="weekly-menu"
                        anchorEl={deleteMenuAnchorEl}
                        open={deleteMenuOpen}
                        onClose={handleDeleteMenuClose}
                        MenuListProps={{
                          "aria-labelledby": "weekly-button",
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: theme.spacing(1),
                              padding: theme.spacing(4),
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="h5"
                          fontSize={18}
                          fontWeight={"bold"}
                        >
                          Delete Plan Template
                        </Typography>

                        <Typography
                          variant="body1"
                          fontSize={16}
                          marginTop={theme.spacing(1)}
                        >
                          Are you sure you want to delete this plan template?
                        </Typography>

                        <Grid
                          container
                          item
                          xs={12}
                          justifyContent="space-between"
                          marginTop={theme.spacing(2)}
                        >
                          <Grid item>
                            <Button
                              color="secondary"
                              className={` ${classes.button} ${classes.deleteButton}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMenuClose(e);
                              }}
                            >
                              Cancel
                            </Button>
                          </Grid>

                          <Grid item>
                            <LoadingButton
                              variant="contained"
                              color="primary"
                              className={classes.button}
                              label={"Delete Plan Template"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlanTemplate(e);
                              }}
                              loading={deletePlanTemplateLoading}
                            />
                          </Grid>
                        </Grid>
                      </Menu>
                    </IconButton>
                  </Tooltip>
                </Grid>
              ),
            },
          ]}
          loading={isFetching}
          disableSelectionOnClick
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            setSortModel(newSortModel);
          }}
          getRowId={(row) => row.id}
          page={page}
          // rowCount={planTemplates?.data?.total || 0}
          pageSize={limit}
          onPageChange={(newPage) => {
            setPage(newPage);
            setOffset(newPage * limit);
          }}
          rowsPerPageOptions={[]}
        />
      </Grid>
      <PlanTemplateModal
        open={planTemplateModalOpen}
        onClose={handleClosePlanTemplateModal}
        planTemplate={selectedPlanTemplate}
        isEditing={isEditing}
      />
    </AthletesLayout>
  );
};

export default PlanTemplates;
